 
import { API_BASE_URL, DEFAULT_HEADERS } from '../constants/api-constants';
import { getAccessToken } from '@/services/api/auth-service';
import type { ApiResponse } from '../types';
import type { FetchRequestOptions } from '@/types/application/common/utilities/fetch-utilities';

export class FetchError extends Error {
  status: number;
  data?: unknown;

  constructor(message: string, status: number, data?: unknown) {
    super(message);
    this.name = 'FetchError';
    this.status = status;
    this.data = data;
  }
}

/**
 * Execute a fetch request to the API
 */
export async function fetchApi<T>(
  endpoint: string,
  options: FetchRequestOptions = {}
): Promise<ApiResponse<T>> {
  const { method = 'GET', body, headers = {}, useAuth = true } = options;

  const url = `${API_BASE_URL}${endpoint}`;
  
  // For DELETE requests, use minimal headers (only Authorization) to match curl behavior
  // DELETE with Authorization will trigger CORS preflight, but we keep headers minimal
  let requestHeaders: Record<string, string>;
  
  if (method === 'DELETE') {
    // DELETE: minimal headers; add Content-Type when a body is sent (e.g. KB tag force delete)
    requestHeaders = {};
    if (body !== undefined && body !== null) {
      requestHeaders['Content-Type'] = DEFAULT_HEADERS['Content-Type'];
    }
  } else {
    // Other methods: Include custom headers and add Content-Type if needed
    requestHeaders = { ...headers };
    
    // Add Content-Type for methods that typically have bodies
    if (body && (method === 'POST' || method === 'PATCH' || method === 'PUT')) {
      requestHeaders['Content-Type'] = DEFAULT_HEADERS['Content-Type'];
    } else if (method === 'POST' || method === 'PATCH' || method === 'PUT') {
      // Some APIs expect Content-Type even without body
      requestHeaders['Content-Type'] = DEFAULT_HEADERS['Content-Type'];
    }
  }

  // Add auth token if needed
  if (useAuth) {
    try {
      const token = await getAccessToken();
      
      if (token) {
        requestHeaders['Authorization'] = `Bearer ${token}`;
      } else {
        console.warn('fetchApi: No access token available for authenticated request', { 
          endpoint,
          method,
          url,
          note: 'User may need to re-authenticate.'
        });
      }
    } catch (error) {
      console.error('fetchApi: Error getting access token', {
        error,
        endpoint,
        method,
        url
      });
    }
  }

  const fetchOptions: RequestInit = {
    method,
    headers: requestHeaders,
    redirect: 'follow',
    credentials: 'omit',
  };

  // GET never has a body; other methods may (including DELETE when the API expects JSON, e.g. { force: true })
  if (body !== undefined && body !== null && method !== 'GET') {
    fetchOptions.body = JSON.stringify(body);
  }

  const response = await fetch(url, fetchOptions);

  // Handle no-content responses (204)
  if (response.status === 204) {
    return {
      data: undefined as T,
      success: true,
      status: response.status,
    };
  }

  // Parse JSON response
  let data: T;
  try {
    const text = await response.text();
    data = text ? JSON.parse(text) : undefined as T;
  } catch (error) {
    if (!response.ok) {
      throw new FetchError(
        `Failed to parse error response: ${response.statusText}`,
        response.status,
        { originalError: error }
      );
    }
    throw new FetchError(
      'Failed to parse JSON response',
      response.status,
      { originalError: error }
    );
  }

  if (!response.ok) {
    const errorMessage = (data as { message?: string; error?: string })?.message || (data as { error?: string })?.error || response.statusText;
    
    throw new FetchError(
      errorMessage,
      response.status,
      data
    );
  }

  return {
    data: data as T,
    success: true,
    status: response.status,
  };
}

/**
 * Fetch JSON from a URL (for presigned S3 URLs)
 * Exact implementation matching ignite-frontend's fetchJsonDetails
 * Uses simple fetch without custom headers to avoid CORS preflight requests
 */
export async function fetchJson<T>(url: string): Promise<T> {
  // Exact match to ignite-frontend's fetchJsonDetails implementation
  const response = await fetch(url, {
    method: 'GET',
  });

  // Handle no-content responses (matches ignite-frontend)
  if (response.status === 204) {
    return undefined as T;
  }

  // Parse JSON first, then check response.ok (matches ignite-frontend order)
  let data: T;
  try {
    data = await response.json();
  } catch (error) {
    throw new FetchError(
      'Failed to parse JSON response',
      response.status,
      { originalError: error }
    );
  }

  // Check response.ok after parsing (matches ignite-frontend)
  if (!response.ok) {
    throw new FetchError(
      `Failed to fetch data: ${response.statusText}`,
      response.status,
      { data }
    );
  }

  return data;
}

export default {
  fetchApi,
  fetchJson,
  FetchError,
};
